import { memo, useMemo, useState } from 'react';
import {
  FileText,
  Folder,
  Tag as TagIcon,
  BookOpen,
  Briefcase,
  ChevronRight,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import { Tag } from '@components/ui';
import { Breadcrumb } from './Breadcrumb';
import type {
  Occupation,
  OccupationGroup,
  Skill,
  SkillGroup,
  TaxonomyData,
} from '@/types';
import {
  getBreadcrumbPath,
  getRelatedSkills,
  getRelatedOccupations,
  getOccupationGroupChildren,
  getSkillGroupChildren,
  isUnseenEconomy,
} from '@/services';

type Entity = Occupation | OccupationGroup | Skill | SkillGroup;

interface DetailPanelProps {
  item: Entity | null;
  taxonomyData: TaxonomyData | null;
  onNavigate: (id: string, type: string) => void;
  onShowNetwork?: (id: string, type: 'occupation' | 'skill', label: string, code: string) => void;
  isLoading?: boolean;
  // False until tier 2 (occupation_skill_relations) has streamed in.
  // While false, the related-items sections show a loading pill instead
  // of being silently empty, and the network CTA is disabled.
  isFullDataLoaded?: boolean;
}

function DetailPanelComponent({
  item,
  taxonomyData,
  onNavigate,
  onShowNetwork,
  isLoading = false,
  isFullDataLoaded = true,
}: DetailPanelProps) {
  // Get breadcrumb path
  const breadcrumbPath = useMemo(() => {
    if (!item || !taxonomyData) return [];
    return getBreadcrumbPath(
      taxonomyData,
      item.id,
      item.entityType as 'occupation' | 'occupationGroup' | 'skill' | 'skillGroup'
    );
  }, [item, taxonomyData]);

  // Get related items
  const relatedSkills = useMemo(() => {
    if (!item || !taxonomyData || item.entityType !== 'occupation') return [];
    return getRelatedSkills(taxonomyData, item.id);
  }, [item, taxonomyData]);

  const relatedOccupations = useMemo(() => {
    if (!item || !taxonomyData || item.entityType !== 'skill') return [];
    return getRelatedOccupations(taxonomyData, item.id);
  }, [item, taxonomyData]);

  // Get children for groups
  const occupationGroupChildren = useMemo(() => {
    if (!item || !taxonomyData || item.entityType !== 'occupationGroup') {
      return { subGroups: [], occupations: [] };
    }
    return getOccupationGroupChildren(taxonomyData, item.id);
  }, [item, taxonomyData]);

  const skillGroupChildren = useMemo(() => {
    if (!item || !taxonomyData || item.entityType !== 'skillGroup') {
      return { subGroups: [], skills: [] };
    }
    return getSkillGroupChildren(taxonomyData, item.id);
  }, [item, taxonomyData]);

  // Track when network CTA is clicked to show loading
  const [isNetworkLoading, setIsNetworkLoading] = useState(false);

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
        <div className="h-8 w-3/4 animate-pulse rounded bg-gray-200" />
        <div className="space-y-2">
          <div className="h-4 animate-pulse rounded bg-gray-200" />
          <div className="h-4 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  // Empty state
  if (!item) {
    return (
      <div className="flex h-full flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 rounded-full bg-soft-green p-4">
          <FileText className="h-8 w-8 text-green-3" />
        </div>
        <h3 className="mb-2 text-lg font-semibold text-oxford-blue">
          Select an item
        </h3>
        <p className="max-w-sm text-text-muted">
          Click on an item in the tree to view its details, including description,
          alternative labels, and related items.
        </p>
      </div>
    );
  }

  // Determine item type info
  const isOccupation = item.entityType === 'occupation';
  const isSkill = item.entityType === 'skill';
  const isGroup = item.entityType === 'occupationGroup' || item.entityType === 'skillGroup';

  // Get type-specific fields
  const skillType = isSkill ? (item as Skill).skillType : null;

  // Added local item = code contains "_" (e.g. "1112_1", "8322_8")
  const isAddedItem = item.code.includes('_');

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb path={breadcrumbPath} onNavigate={onNavigate} />

      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`rounded-lg p-2 ${isGroup ? 'bg-soft-green' : 'bg-light-green'}`}>
            {isGroup ? (
              <Folder className="h-6 w-6 text-green-3" />
            ) : isOccupation ? (
              <Briefcase className="h-6 w-6 text-green-3" />
            ) : (
              <BookOpen className="h-6 w-6 text-green-3" />
            )}
          </div>

          <div className="flex-1 space-y-1">
            {/* Code */}
            <p className="font-mono text-sm text-text-muted">{item.code}</p>

            {/* Label */}
            <h1 className="text-xl font-bold text-oxford-blue">
              {item.preferredLabel}
            </h1>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 pt-1">
              {isOccupation && (
                <Tag variant={isUnseenEconomy(item.code) ? 'unseen' : 'seen'}>
                  {isUnseenEconomy(item.code) ? 'Unseen Economy' : 'Seen Economy'}
                </Tag>
              )}
              {skillType && (
                <Tag variant="skill">
                  {formatSkillType(skillType)}
                </Tag>
              )}
              {isGroup && (
                <Tag variant="default">Group</Tag>
              )}
              {isAddedItem && (
                <Tag variant="localized">
                  Added {isOccupation ? 'Occupation' : 'Skill'}
                </Tag>
              )}
              {item.isLocalized && !isAddedItem && (
                <Tag variant="localized">
                  Localized
                </Tag>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Network CTA - only for occupations and skills (not groups) */}
      {(isOccupation || isSkill) && onShowNetwork && (
        <div className="rounded-lg bg-soft-green p-4">
          <button
            type="button"
            onClick={() => {
              setIsNetworkLoading(true);
              onShowNetwork(
                item.id,
                isOccupation ? 'occupation' : 'skill',
                item.preferredLabel,
                item.code
              );
            }}
            disabled={isNetworkLoading || !isFullDataLoaded}
            className="inline text-sm font-medium leading-relaxed text-green-3 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="inline-flex items-center gap-2 bg-tabiya-green px-1 text-oxford-blue transition-colors hover:bg-oxford-blue hover:text-white">
              See how this {isOccupation ? 'occupation' : 'skill'} connects to the broader taxonomy →
              {(isNetworkLoading || !isFullDataLoaded) && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
            </span>
          </button>
          {!isFullDataLoaded && (
            <p className="mt-2 text-xs text-text-muted">
              Loading connection data…
            </p>
          )}
        </div>
      )}

      {/* Description */}
      {item.description && (
        <section>
          <h2 className="mb-2 text-base font-semibold uppercase tracking-wide text-text-muted">
            Description
          </h2>
          <p className="whitespace-pre-wrap text-sm text-oxford-blue">{truncateToSentences(item.description, 2)}</p>
        </section>
      )}


      {/* Alternative Labels */}
      {item.altLabels.length > 0 && (
        <section>
          <h2 className="mb-2 text-base font-semibold uppercase tracking-wide text-text-muted">
            Alternative Names
          </h2>
          <div className="flex flex-wrap gap-2">
            {item.altLabels.map((label, index) => {
              const isAdded = item.addedAltLabels.has(label.toLowerCase());
              return (
                <span
                  key={index}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm text-oxford-blue ${
                    isAdded ? 'bg-soft-green' : 'bg-tabiya-gray'
                  }`}
                >
                  <TagIcon className="h-3 w-3 text-text-muted" />
                  {label}
                </span>
              );
            })}
          </div>
        </section>
      )}

      {/* Related Skills (for occupations) */}
      {isOccupation && !isFullDataLoaded && relatedSkills.length === 0 && (
        <section>
          <h2 className="mb-2 text-base font-semibold uppercase tracking-wide text-text-muted">
            Related Skills
          </h2>
          <p className="inline-flex items-center gap-2 text-sm text-text-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading related skills…
          </p>
        </section>
      )}
      {relatedSkills.length > 0 && (
        <section>
          <h2 className="mb-2 text-base font-semibold uppercase tracking-wide text-text-muted">
            Related Skills ({relatedSkills.length})
          </h2>
          {(() => {
            const hasSignalling = relatedSkills.some(r => r.signallingValueLabel);
            const hasRelationType = relatedSkills.some(r => r.relationType === 'essential' || r.relationType === 'optional');

            if (hasSignalling) {
              // Group by signalling value (high/medium/low)
              return (
                <div className="space-y-4">
                  {(['high', 'medium', 'low'] as const).map(level => {
                    const items = relatedSkills.filter(r => r.signallingValueLabel === level);
                    if (items.length === 0) return null;
                    const styles = { high: 'border-blue-500 from-blue-50 text-blue-600', medium: 'border-green-500 from-green-50 text-green-600', low: 'border-gray-400 from-gray-50 text-gray-600' };
                    const labels = { high: 'High Signalling Value', medium: 'Medium Signalling Value', low: 'Low Signalling Value' };
                    return (
                      <div key={level} className={`border-l-4 bg-gradient-to-r to-transparent pl-4 ${styles[level]}`}>
                        <p className={`mb-2 text-sm font-semibold ${styles[level].split(' ').pop()}`}>{labels[level]}</p>
                        <ExpandableList
                          items={items.map(({ skill }) => ({ id: skill.id, label: skill.preferredLabel }))}
                          type="skill" onNavigate={onNavigate} initialCount={15}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            }

            if (hasRelationType) {
              // Group by essential/optional
              return (
                <div className="space-y-4">
                  {relatedSkills.filter(r => r.relationType === 'essential').length > 0 && (
                    <div>
                      <p className="mb-2 text-sm font-medium text-green-3">Essential</p>
                      <ExpandableList
                        items={relatedSkills.filter(r => r.relationType === 'essential').map(({ skill }) => ({ id: skill.id, label: skill.preferredLabel }))}
                        type="skill" onNavigate={onNavigate} initialCount={15}
                      />
                    </div>
                  )}
                  {relatedSkills.filter(r => r.relationType === 'optional').length > 0 && (
                    <div>
                      <p className="mb-2 text-sm font-medium text-text-muted">Optional</p>
                      <ExpandableList
                        items={relatedSkills.filter(r => r.relationType === 'optional').map(({ skill }) => ({ id: skill.id, label: skill.preferredLabel }))}
                        type="skill" onNavigate={onNavigate} initialCount={10}
                      />
                    </div>
                  )}
                </div>
              );
            }

            // No grouping — show all skills in a flat list
            return (
              <ExpandableList
                items={relatedSkills.map(({ skill }) => ({ id: skill.id, label: skill.preferredLabel }))}
                type="skill" onNavigate={onNavigate} initialCount={20}
              />
            );
          })()}
        </section>
      )}

      {/* Related Occupations (for skills) */}
      {isSkill && !isFullDataLoaded && relatedOccupations.length === 0 && (
        <section>
          <h2 className="mb-2 text-base font-semibold uppercase tracking-wide text-text-muted">
            Related Occupations
          </h2>
          <p className="inline-flex items-center gap-2 text-sm text-text-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading related occupations…
          </p>
        </section>
      )}
      {relatedOccupations.length > 0 && (
        <section>
          <h2 className="mb-2 text-base font-semibold uppercase tracking-wide text-text-muted">
            Related Occupations ({relatedOccupations.length})
          </h2>
          <ExpandableList
            items={relatedOccupations.map(({ occupation }) => ({
              id: occupation.id,
              label: occupation.preferredLabel,
            }))}
            type="occupation"
            onNavigate={onNavigate}
            initialCount={20}
          />
        </section>
      )}

      {/* Sub-groups for occupation groups */}
      {item.entityType === 'occupationGroup' && occupationGroupChildren.subGroups.length > 0 && (
        <section>
          <h2 className="mb-3 text-base font-semibold uppercase tracking-wide text-text-muted">
            Sub-groups ({occupationGroupChildren.subGroups.length})
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {occupationGroupChildren.subGroups.map((group) => (
              <GroupCard
                key={group.id}
                id={group.id}
                code={group.code}
                label={group.preferredLabel}
                description={group.description}
                type="occupationGroup"
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </section>
      )}

      {/* Child occupations for occupation groups */}
      {item.entityType === 'occupationGroup' && occupationGroupChildren.occupations.length > 0 && (
        <section>
          <h2 className="mb-2 text-base font-semibold uppercase tracking-wide text-text-muted">
            Occupations ({occupationGroupChildren.occupations.length})
          </h2>
          <ExpandableList
            items={occupationGroupChildren.occupations.map((occ) => ({
              id: occ.id,
              label: occ.preferredLabel,
            }))}
            type="occupation"
            onNavigate={onNavigate}
            initialCount={30}
          />
        </section>
      )}

      {/* Sub-groups for skill groups */}
      {item.entityType === 'skillGroup' && skillGroupChildren.subGroups.length > 0 && (
        <section>
          <h2 className="mb-3 text-base font-semibold uppercase tracking-wide text-text-muted">
            Sub-groups ({skillGroupChildren.subGroups.length})
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {skillGroupChildren.subGroups.map((group) => (
              <GroupCard
                key={group.id}
                id={group.id}
                code={group.code}
                label={group.preferredLabel}
                description={group.description}
                type="skillGroup"
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </section>
      )}

      {/* Child skills for skill groups */}
      {item.entityType === 'skillGroup' && skillGroupChildren.skills.length > 0 && (
        <section>
          <h2 className="mb-2 text-base font-semibold uppercase tracking-wide text-text-muted">
            Skills ({skillGroupChildren.skills.length})
          </h2>
          <ExpandableList
            items={skillGroupChildren.skills.map((skill) => ({
              id: skill.id,
              label: skill.preferredLabel,
            }))}
            type="skill"
            onNavigate={onNavigate}
            initialCount={30}
          />
        </section>
      )}
    </div>
  );
}

// Helper component for related item links - inline style for comma-separated list
interface RelatedItemLinkProps {
  id: string;
  label: string;
  type: 'occupation' | 'skill';
  onNavigate: (id: string, type: string) => void;
  isLast?: boolean;
}

function RelatedItemLink({ id, label, type, onNavigate, isLast = false }: RelatedItemLinkProps) {
  return (
    <span className="inline">
      <button
        type="button"
        onClick={() => onNavigate(id, type)}
        className="text-sm text-oxford-blue transition-colors hover:bg-tabiya-green hover:text-oxford-blue"
      >
        {label}
      </button>
      {!isLast && <span className="text-oxford-blue">, </span>}
    </span>
  );
}

// Expandable list component
interface ExpandableListProps {
  items: { id: string; label: string }[];
  type: 'occupation' | 'skill';
  onNavigate: (id: string, type: string) => void;
  initialCount?: number;
}

function ExpandableList({ items, type, onNavigate, initialCount = 15 }: ExpandableListProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const displayedItems = isExpanded ? items : items.slice(0, initialCount);
  const hasMore = items.length > initialCount;

  return (
    <>
      <div className="leading-relaxed">
        {displayedItems.map((item, index) => (
          <RelatedItemLink
            key={item.id}
            id={item.id}
            label={item.label}
            type={type}
            onNavigate={onNavigate}
            isLast={index === displayedItems.length - 1 && !hasMore}
          />
        ))}
        {hasMore && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="ml-1 inline-flex items-center gap-1 text-xs font-medium text-green-3 transition-colors hover:text-oxford-blue"
          >
            {isExpanded ? (
              <>
                <ChevronDown className="h-3 w-3" />
                Show less
              </>
            ) : (
              <>
                <ChevronRight className="h-3 w-3" />
                +{items.length - initialCount} more
              </>
            )}
          </button>
        )}
      </div>
    </>
  );
}

// Helper component for group cards
interface GroupCardProps {
  id: string;
  code: string;
  label: string;
  description: string;
  type: 'occupationGroup' | 'skillGroup';
  onNavigate: (id: string, type: string) => void;
}

function GroupCard({ id, code, label, description, type, onNavigate }: GroupCardProps) {
  return (
    <button
      type="button"
      onClick={() => onNavigate(id, type)}
      className="group flex w-full flex-col gap-1 rounded-lg border border-gray-200 bg-tabiya-gray p-4 text-left transition-all hover:-translate-y-0.5 hover:border-green-3 hover:bg-soft-green"
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-medium text-green-3">{code}</span>
        <ChevronRight className="h-4 w-4 text-text-muted opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
      <span className="font-medium text-oxford-blue">{label}</span>
      {description && (
        <span className="line-clamp-2 text-sm text-text-muted">
          {description.substring(0, 120)}
          {description.length > 120 ? '...' : ''}
        </span>
      )}
    </button>
  );
}

// Truncate description before "Tasks include" or similar patterns
function truncateToSentences(text: string, _maxSentences: number = 3): string {
  // First, try to truncate before "Tasks include" or similar
  const tasksPattern = /\s*Tasks include[^.]*\.?/i;
  const tasksIndex = text.search(tasksPattern);

  if (tasksIndex > 0) {
    return text.substring(0, tasksIndex).trim();
  }

  // Fallback: return full text (no "Tasks include" found)
  return text;
}

// Format skill type for display
function formatSkillType(skillType: string): string {
  const types: Record<string, string> = {
    'skill/competence': 'Skill/Competence',
    'knowledge': 'Knowledge',
    'language': 'Language',
    'transversal': 'Transversal',
  };
  return types[skillType] || skillType;
}

export const DetailPanel = memo(DetailPanelComponent);
